import { useState } from 'react';
import Modal from './Modal';
import type { AppSettings, Goal, StrategicPriority } from '../types';

export interface GoalDraft {
  title: string;
  councilDescription: string;
  districtDescription: string;
  committee: string;
  districtIds: string[];
  strategicPriorityId: string;
}

export default function GoalForm({
  initial,
  priorities,
  settings,
  onClose,
  onSave,
}: {
  initial: Goal | null;
  priorities: StrategicPriority[];
  settings: AppSettings;
  onClose: () => void;
  onSave: (draft: GoalDraft) => Promise<void>;
}) {
  const [d, setD] = useState<GoalDraft>(() => ({
    title: initial?.title ?? '',
    councilDescription: initial?.councilDescription ?? '',
    districtDescription: initial?.districtDescription ?? '',
    committee: initial?.committee ?? settings.committees[0]?.id ?? '',
    districtIds: initial?.districtIds ?? settings.districts.map((x) => x.id),
    strategicPriorityId: initial?.strategicPriorityId ?? priorities[0]?.id ?? '',
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof GoalDraft>(k: K, v: GoalDraft[K]) =>
    setD((p) => ({ ...p, [k]: v }));

  const toggleDistrict = (id: string) =>
    set(
      'districtIds',
      d.districtIds.includes(id)
        ? d.districtIds.filter((x) => x !== id)
        : [...d.districtIds, id],
    );

  return (
    <Modal title={initial ? 'Edit Goal' : 'New Goal'} onClose={onClose}>
      <label>Strategic Area</label>
      <select
        value={d.strategicPriorityId}
        onChange={(e) => set('strategicPriorityId', e.target.value)}
      >
        {priorities.length === 0 && <option value="">— create a Strategic Area first —</option>}
        {priorities.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title}
          </option>
        ))}
      </select>

      <label>Title</label>
      <input value={d.title} onChange={(e) => set('title', e.target.value)} />

      <label>Council actions — what council itself is doing</label>
      <textarea
        rows={3}
        placeholder={'One point per line — each line shows as its own bullet.'}
        value={d.councilDescription}
        onChange={(e) => set('councilDescription', e.target.value)}
      />

      <label>District focus — what each district is asked to do</label>
      <textarea
        rows={3}
        placeholder={'One point per line — each line shows as its own bullet.'}
        value={d.districtDescription}
        onChange={(e) => set('districtDescription', e.target.value)}
      />

      <label>Committee</label>
      <select value={d.committee} onChange={(e) => set('committee', e.target.value)}>
        {settings.committees.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
      </select>

      <label>Applies to districts</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {settings.districts.map((x) => (
          <label key={x.id} style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={d.districtIds.includes(x.id)}
              onChange={() => toggleDistrict(x.id)}
            />
            {x.name}
          </label>
        ))}
      </div>

      {err && <div className="err">{err}</div>}

      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={busy || !d.title.trim() || !d.strategicPriorityId || d.districtIds.length === 0}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await onSave({
                ...d,
                title: d.title.trim(),
                councilDescription: d.councilDescription.trim(),
                districtDescription: d.districtDescription.trim(),
              });
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Could not save that Goal.');
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </button>
      </div>
    </Modal>
  );
}
